import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Box,
  Paper,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderType } from '../../../app/data/models/orders/orderType';
import { formatSwissPhoneNumber } from '../../../app/utils/phoneNumberValidator';
import { useEffect, useState } from 'react';
import { useAppSelector } from '../../../app/store/configureStore';
import api from '../../../app/api/api';
import { RestaurantTable } from '../../manager/tables/types';

interface Props {
  orderType: OrderType;
  tableNumber: number | undefined;
  customerName: string | undefined;
  customerPhone: string | undefined;
  onOrderTypeChange: (type: OrderType) => void;
  onTableNumberChange: (tableNumber: number | undefined) => void;
  onCustomerNameChange: (name: string) => void;
  onCustomerPhoneChange: (phoneNumber: string) => void;
  phoneNumberError: string | null;
  customerNameError: string | null;
  tableNumberError: string | null;
}

export default function OrderTypeSelector({
  orderType,
  tableNumber,
  customerName,
  customerPhone,
  onOrderTypeChange,
  onTableNumberChange,
  onCustomerNameChange,
  onCustomerPhoneChange,
  phoneNumberError,
  customerNameError,
  tableNumberError,
}: Props) {
  const { t } = useTranslation();
  const { cart } = useAppSelector((state) => state.cart);
  const restaurantId = cart.restaurantId;
  const [tables, setTables] = useState<RestaurantTable[]>([]);

  useEffect(() => {
    if (restaurantId) {
      api.CustomerRestaurant.tables(restaurantId)
        .then((response) => {
          setTables(response);
        })
        .catch((error) => {
          console.error('Failed to fetch tables:', error);
        });
    }
  }, [restaurantId]);

  const handleCustomerPhoneChange = (value: string) => {
    const formattedNumber = formatSwissPhoneNumber(value);
    onCustomerPhoneChange(formattedNumber);
  };

  return (
    <Paper
      sx={{
        p: 3,
        mb: 2,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {t('cart.order-type.title')}
      </Typography>
      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          value={OrderType[orderType]}
          onChange={(e) =>
            onOrderTypeChange(
              OrderType[e.target.value as keyof typeof OrderType]
            )
          }
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 2,
            }}
          >
            <FormControlLabel
              value={OrderType[OrderType.DineIn]}
              control={<Radio />}
              label={t('cart.order-type.dine-in')}
              sx={{
                flex: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                m: 0,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            />
            <FormControlLabel
              value={OrderType[OrderType.TakeAway]}
              control={<Radio />}
              label={t('cart.order-type.takeaway')}
              sx={{
                flex: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                m: 0,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            />
          </Box>
        </RadioGroup>

        <Box sx={{ mt: 2 }}>
          {orderType === OrderType.DineIn && (
            <FormControl
              fullWidth
              required
              error={!!tableNumberError}
              size="small"
            >
              <InputLabel id="table-number-select-label">
                {t('cart.table-number')}
              </InputLabel>
              <Select
                labelId="table-number-select-label"
                value={tableNumber || ''}
                onChange={(e) =>
                  onTableNumberChange(
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                label={t('cart.table-number')}
              >
                {tables.map((table) => (
                  <MenuItem key={table.id} value={table.tableNumber}>
                    {table.tableNumber}
                  </MenuItem>
                ))}
              </Select>
              {tableNumberError && (
                <Typography variant="caption" color="error">
                  {tableNumberError}
                </Typography>
              )}
            </FormControl>
          )}

          {orderType === OrderType.TakeAway && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={t('cart.customer-name')}
                value={customerName}
                onChange={(e) => onCustomerNameChange(e.target.value)}
                error={!!customerNameError}
                helperText={customerNameError || t('cart.customer-name-helper')}
                fullWidth
                required
                placeholder={t('cart.customer-name-placeholder')}
                size="small"
              />
              <TextField
                label={t('cart.phone-number')}
                value={customerPhone}
                onChange={(e) => handleCustomerPhoneChange(e.target.value)}
                error={!!phoneNumberError}
                helperText={phoneNumberError || t('cart.phone-number-helper')}
                fullWidth
                required
                placeholder="+41 XX XXX XX XX"
                size="small"
              />
            </Box>
          )}
        </Box>
      </FormControl>
    </Paper>
  );
}
